// The workload number that sits beside a candidate's name while work is being
// assigned (layer 3).

import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatPointsValue, pluralizeTasks } from './formatPoints';
import type {
  ClinicianLoad,
  SchedulingPointLoadWeek,
} from '../../services/api/types.gen';

export interface ClinicianLoadBadgeProps {
  /** The week these loads describe, or undefined when there is nothing to say. */
  week: SchedulingPointLoadWeek | undefined;
  /** Every candidate's load for that week, keyed by person. */
  loadsByUserId: Map<string, ClinicianLoad>;
  /** The person this option stands for. */
  userId: string | null | undefined;
}

/**
 * How many tasks this person's number is built from, and how many exist.
 *
 * `uncounted_task_count` is tasks that are real but whose type nobody has
 * priced (or that the clinic switched off). They sit outside `points` entirely,
 * so the honest total is the sum — and the gap between the two is exactly the
 * thing a scheduler must be told, because otherwise a busy week reads as a
 * light one and the number quietly lies in the direction of overloading someone.
 */
const describeTasks = (load: ClinicianLoad): string => {
  const total = load.task_count + load.uncounted_task_count;
  if (total === 0) return 'no tasks';
  if (load.uncounted_task_count > 0) {
    return `${load.task_count} of ${pluralizeTasks(total)}`;
  }
  return pluralizeTasks(load.task_count);
};

/** The same fact, said in full for a reader who has no dropdown row for context. */
const describeTasksForScreenReader = (load: ClinicianLoad): string => {
  const total = load.task_count + load.uncounted_task_count;
  if (total === 0) return 'no tasks';
  if (load.uncounted_task_count > 0) {
    return `${load.task_count} of ${pluralizeTasks(total)} counted`;
  }
  return pluralizeTasks(load.task_count);
};

/**
 * `31.5 · 6 of 8 tasks` beside a name in an assignee picker.
 *
 * Three absences, told apart deliberately:
 *
 * - No week at all (no permission, no clinic, no date, request failed) — render
 *   nothing. The picker is then exactly the picker that shipped before this
 *   feature existed, which is what a failure here must cost.
 * - A week, but this candidate is not in it. That is a real fact — they were
 *   force-included from another clinic — so it is *said*, not left blank.
 * - A week, this candidate in it, carrying nothing. `0 · no tasks`. Never a
 *   dash: an empty week is one of the most useful things this can tell a
 *   scheduler, and rendering it as missing data throws that away.
 */
export const ClinicianLoadBadge = ({
  week,
  loadsByUserId,
  userId,
}: ClinicianLoadBadgeProps) => {
  if (!week || !userId) return null;

  const load = loadsByUserId.get(userId);

  if (!load) {
    return (
      <span className='whitespace-nowrap text-xs text-muted-foreground'>
        not in this clinic
      </span>
    );
  }

  const points = formatPointsValue(load.points);

  return (
    <span
      className='whitespace-nowrap text-xs tabular-nums text-muted-foreground'
      aria-label={`${points} ${week.unit_label}, ${describeTasksForScreenReader(
        load
      )}, week of ${formatDateForDisplay(week.week_start)}`}
    >
      {points} · {describeTasks(load)}
    </span>
  );
};

export default ClinicianLoadBadge;
